<script setup lang="ts">
import ReloadPrompt from "@/components/ReloadPrompt.vue";
import NewsletterSubscribeDialog from "@/components/dialogs/NewsletterSubscribeDialog.vue";
import { useNewsletterDialog } from "@/composables/useNewsletterDialog";
import InactivityDialog from "@/components/dialogs/InactivityDialog.vue";
import TermsReacceptanceDialog from "@/components/dialogs/TermsReacceptanceDialog.vue";
import LoggedOutDialog from "./components/dialogs/LoggedOutDialog.vue";
import { useBayModeStore } from "@/store/useBayModeStore";
import { useBooking } from "@/store/useBooking";
import { useCheckoutStore } from "@/utils/Docket";
import { pb } from "@/utils/PocketBaseAdapter";
import { useUserStore } from "@/utils/UserStore";
import { useUserRole } from "@/utils/useUserRole";
import ScrollToTop from "@core/components/ScrollToTop.vue";
import { useThemeConfig } from "@core/composable/useThemeConfig";
import { hexToRgb } from "@layouts/utils";
import { useRoute, useRouter } from "vue-router";
import { useTheme } from "vuetify";
import bookingGlobalPopup from "./components/booking/bookingGlobalPopup.vue";

const {
  theme,
  syncInitialLoaderTheme,
  syncVuetifyThemeWithTheme: syncConfigThemeWithVuetifyTheme,
  isAppRtl,
  handleSkinChanges,
} = useThemeConfig();

const { global } = useTheme();
const route = useRoute();
const router = useRouter();
const checkoutStore = useCheckoutStore();
const { docketNumber } = storeToRefs(checkoutStore);

const bayModeStore = useBayModeStore();
const userRoleStore = useUserRole();
const userStore = useUserStore();
const bookingStore = useBooking();
const { clearBookingStore } = bookingStore;
const { showNewsletterDialog } = useNewsletterDialog();

// ℹ️ Sync current theme with initial loader theme
syncInitialLoaderTheme();
syncConfigThemeWithVuetifyTheme();
handleSkinChanges();

const monitorDocketTable = async () => {
  try {
    pb.collection("dockets").subscribe("*", async (dkt) => {
      setTimeout(() => {
        let docketPrint = JSON.parse(
          localStorage.getItem("printDocket") || "{}"
        );
        if (
          dkt &&
          (dkt.record.id == route?.query?.did ||
            dkt.record.id == docketPrint.id)
        ) {
          // update the docketNumber to show the correct one
          docketNumber.value = dkt.record.docketNumber;
        }
      }, 500);
    });
  } catch (e) {
    console.log(e);
  }
};

onMounted(async () => {
  await monitorDocketTable();

  // BayMode: detect ?area=N query param
  const areaParam = route.query.area;
  if (areaParam !== undefined) {
    const areaId = areaParam;
    if (pb.authStore.isValid) {
      pb.authStore.clear();
      userRoleStore.$reset();
      clearBookingStore();
    }
    bayModeStore.activate(areaId);

    // Remove ?area from URL without reloading
    router.replace({ query: { ...route.query, area: undefined } });
  }

  // Get the theme from url
  if (route?.query?.theme) {
    theme.value = route?.query?.theme;
  } else {
    await userStore.syncUserThemePreference(pb.authStore.model as any);
  }

  // Set currentLocation with priority:
  // 1. Bay mode area location  2. ?location= URL param  3. homeLocation  4. localStorage
  if (bayModeStore.isActive && bayModeStore.areaId) {
    try {
      const area = await pb.collection("areas").getOne(bayModeStore.areaId as string, { requestKey: null });
      if (area?.location) {
        userStore.currentLocation = area.location;
      }
    } catch (e) {
      console.error("Failed to fetch bay mode area location", e);
    }
  } else if (route?.query?.location) {
    userStore.currentLocation = route?.query?.location;
  }
});

</script>

<template>
  <VLocaleProvider :rtl="isAppRtl">
    <!-- ℹ️ This is required to set the background color of active nav link based on currently active global theme's primary -->
    <VApp
      :style="`--v-global-theme-primary: ${hexToRgb(
        global.current.value.colors.primary
      )}`"
    >
      <RouterView />
      <ScrollToTop />
    </VApp>

    <booking-global-popup />
    <NewsletterSubscribeDialog v-model="showNewsletterDialog" />
    <reload-prompt />
    <InactivityDialog />
    <LoggedOutDialog />
    <TermsReacceptanceDialog />
  </VLocaleProvider>
</template>
